import React from 'react';
import { FONT_SIZE, FONT_WEIGHT, SPACING } from '../../constants/constants';
import { onActivateKey } from '../../utils/a11y';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useIsPhone } from '../../hooks/UseIsCompact';
import type { FilterOption } from '../../hooks/UseCardView';

/** 칩 하나 — `title` 은 칩의 뜻(마우스 올림), `testId` 는 테스트용. */
export type AppFilterChipOption = FilterOption & { title?: string; testId?: string };

interface AppFilterChipsProps {
  /** 왼쪽에 붙는 라벨(예: "레어도"). 없으면 생략. */
  label?: string;
  /** 오른쪽 끝에 구분선과 함께 붙는 안내 문구(예: "클릭하여 필터"). 없으면 생략. */
  hint?: string;
  options: AppFilterChipOption[];
  value:   string[];
  onChange: (next: string[]) => void;
  /**
   * 하나만 고르는 칩(저장 필터 칩 — 가격 목록 개편 PR 3, 감사 card-controltower#697). 다른 칩을 누르면 그것만, 켜진 칩을 다시
   * 누르면 끈다(빈 배열).
   */
  single?: boolean;
  /** 칩 안에 건수(`option.count`)를 보인다. null · 없음이면 건수 자리를 비운다(이 화면에서 모르는 건수). */
  showCount?: boolean;
  /** 한 줄에 두고 넘치면 가로 스크롤(390 — 줄바꿈으로 표를 밀어내지 않게). 기본은 줄바꿈. */
  scroll?: boolean;
  /** 칩 묶음의 이름(스크린리더 — role=group). */
  ariaLabel?: string;
}

/**
 * 클릭으로 켜고 끄는 다중 선택 필터 칩. 체크박스 목록 대신 쓰면 라벨 길이가 제각각이어도
 * 칩마다 자기 크기만큼만 차지해서 줄바꿈돼도 들쭉날쭉해 보이지 않는다 — 옛 가격 필터 바 ·
 * StockToolbar의 레어도 필터가 체크박스일 때 정렬이 흐트러져 보인다는 피드백으로 도입
 * (2026-07-30).
 */
/** 넓은 화면 칩(약 21px)의 44px 누르는 곳이 가로 스크롤 상자에 잘리지 않게 둘 위아래 여백 — 레이아웃 고정값(CLAUDE.md §7 예외). */
const HIT_PAD = 12;

export const AppFilterChips: React.FC<AppFilterChipsProps> = ({
  label, hint, options, value, onChange, single = false, showCount = false, scroll = false, ariaLabel,
}) => {
  const t = useThemeTokens();
  // 휴대폰 폭은 칩마다 누르는 곳 44×44(코덱 화면 점검 2026-10-30 — 27px · C 칩은 폭 25px, 감사 #518). 보이는 필 모양은 그대로 크게.
  const phone = useIsPhone();
  const selected = new Set(value);

  const toggle = (v: string) => {
    if (single) {
      onChange(selected.has(v) ? [] : [v]);
      return;
    }
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(Array.from(next));
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: scroll ? 'nowrap' : 'wrap', minWidth: 0 }}>
      {label && (
        <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, whiteSpace: 'nowrap' }}>{label}</span>
      )}
      <div
        role={ariaLabel ? 'group' : undefined}
        aria-label={ariaLabel}
        style={{
          display: 'flex', flexWrap: scroll ? 'nowrap' : 'wrap', gap: SPACING.xxs,
          ...(scroll ? { overflowX: 'auto' as const, minWidth: 0, maxWidth: '100%' } : {}),
          // 가로 스크롤 상자는 세로도 잘라 칩의 44px 누르는 곳(.app-hit ::after)이 잘린다 — 위아래 여백을 두고 같은 만큼 당겨 줄 높이는 그대로(감사 #693).
          ...(scroll && !phone ? { paddingBlock: HIT_PAD, marginBlock: -HIT_PAD } : {}),
        }}
      >
        {options.map((opt) => {
          const active = selected.has(opt.value);
          const count = showCount && opt.count != null ? opt.count : null;
          return (
            <div
              key={opt.value}
              role="button"
              tabIndex={0}
              // 넓은 화면은 보이는 필 그대로, 누르는 곳만 세로 44px(index.css .app-hit--y — 칩 사이 4px 라 가로로 넓히면 옆 칩을 덮는다, 감사 #861).
              // 휴대폰은 위처럼 필 자체가 44px.
              className={phone ? 'app-focus-ring' : 'app-focus-ring app-hit app-hit--y'}
              aria-pressed={active}
              title={opt.title}
              data-testid={opt.testId}
              onClick={() => toggle(opt.value)}
              onKeyDown={onActivateKey(() => toggle(opt.value))}
              style={{
                padding: `${SPACING.xxs}px ${phone ? SPACING.sm : SPACING.xs}px`,
                ...(phone ? { minHeight: 44, minWidth: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' as const } : {}),
                // 자기 크기에 비례하는 필 모양 — RADIUS 사다리 대상이 아닌 의도적 예외(CLAUDE.md §7).
                borderRadius: '999px',
                border: `1px solid ${active ? t.lineAccent : t.border}`,
                background: active ? t.tintInfo : t.bgRaised,
                color: active ? t.accent : t.textSecondary,
                fontSize: FONT_SIZE.xs,
                fontWeight: active ? FONT_WEIGHT.semibold : FONT_WEIGHT.regular,
                cursor: 'pointer',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
              }}
            >
              {opt.label}
              {count != null && (
                <span style={{ marginLeft: 4, fontVariantNumeric: 'tabular-nums', fontWeight: FONT_WEIGHT.bold }}>
                  {count.toLocaleString()}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {hint && (
        <>
          <div style={{ width: 1, height: 14, background: t.border, flexShrink: 0 }} />
          <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, whiteSpace: 'nowrap' }}>{hint}</span>
        </>
      )}
    </div>
  );
};
